import moment from "moment";
import { SERVICES, PAYROLL_EPOCH, ROLE_ORDER, ROLES } from "./backofficeConstants";

export const DATE = "YYYY-MM-DD";

export function today() {
	return moment().format(DATE);
}

export function addDays(dateStr, days) {
	return moment(dateStr, DATE).add(days, "days").format(DATE);
}

export function fmt(dateStr) {
	if (!dateStr) return "";
	return moment(dateStr, DATE).format("MMM DD");
}

export function fmtLong(dateStr) {
	if (!dateStr) return "";
	return moment(dateStr, DATE).format("ddd, MMM D");
}

export function fmtFull(dateStr) {
	if (!dateStr) return "";
	return moment(dateStr, DATE).format("MMM D, YYYY");
}

export function isOverdue(task, ref = today()) {
	return task.status !== "complete" && task.dueDate < ref;
}

export function isComplete(task) {
	return task.status === "complete";
}

/**
 * The period a date falls in, per service. Must agree with backofficeService.periodFor on the API.
 */
export function periodFor(service, dateStr) {
	const type = (SERVICES[service] || SERVICES.adhoc).periodType;
	const d = moment(dateStr, DATE);
	if (type === "month") {
		return { start: d.clone().startOf("month").format(DATE), end: d.clone().endOf("month").format(DATE) };
	}
	if (type === "week") {
		const start = d.clone().isoWeekday(1);
		return { start: start.format(DATE), end: start.clone().add(6, "days").format(DATE) };
	}
	const epoch = moment(PAYROLL_EPOCH, DATE);
	const block = Math.floor(d.diff(epoch, "days") / 14);
	const start = epoch.clone().add(block * 14, "days");
	return { start: start.format(DATE), end: start.clone().add(13, "days").format(DATE) };
}

export function shiftPeriod(service, period, direction) {
	const type = (SERVICES[service] || SERVICES.adhoc).periodType;
	const start = moment(period.start, DATE);
	if (type === "month") return periodFor(service, start.add(direction, "months").format(DATE));
	if (type === "week") return periodFor(service, start.add(direction * 7, "days").format(DATE));
	return periodFor(service, start.add(direction * 14, "days").format(DATE));
}

export function periodLabel(service, period) {
	const type = (SERVICES[service] || SERVICES.adhoc).periodType;
	const s = moment(period.start, DATE);
	const e = moment(period.end, DATE);
	if (type === "month") return s.format("MMMM YYYY");
	if (type === "week") return "Week of " + s.format("MMM D, YYYY");
	return "Pay period: " + s.format("MMM D") + " – " + e.format("MMM D, YYYY");
}

export function monthOptions(count = 6, from = today()) {
	const out = [];
	const d = moment(from, DATE).startOf("month").subtract(1, "months");
	for (let i = 0; i < count; i++) {
		out.push({ start: d.format(DATE), label: d.format("MMMM YYYY") });
		d.add(1, "months");
	}
	return out;
}

export function periodOptions(service, count = 6, from = today()) {
	const type = (SERVICES[service] || SERVICES.adhoc).periodType;
	if (type === "month") return monthOptions(count, from);
	let period = periodFor(service, from);
	period = shiftPeriod(service, period, -1);
	const out = [];
	for (let i = 0; i < count; i++) {
		out.push({ start: period.start, label: periodLabel(service, period) });
		period = shiftPeriod(service, period, 1);
	}
	return out;
}

export function budgetTotal(task) {
	const b = task.budget || {};
	return (b.preparer || 0) + (b.reviewer || 0) + (b.manager || 0);
}

export function loggedTotal(task) {
	const l = task.logged || {};
	return (l.preparer || 0) + (l.reviewer || 0) + (l.manager || 0);
}

export function hrs(n) {
	return (Math.round((n || 0) * 10) / 10).toFixed(1);
}

export function userName(users, id) {
	const u = users.find(x => x.id === id);
	return u ? u.name : "";
}

export function userShort(users, id) {
	const u = users.find(x => x.id === id);
	if (!u) return "—";
	return `${u.firstName || ""}${u.lastName ? " " + u.lastName[0] + "." : ""}`.trim() || u.name;
}

export function userInitials(users, id) {
	const u = users.find(x => x.id === id);
	return u ? u.initials : "?";
}

export function assignees(task) {
	return ROLE_ORDER.map(r => task[ROLES[r].idField]).filter(Boolean);
}

export function escapeCsv(x) {
	return '"' + String(x === undefined || x === null ? "" : x).replace(/"/g, '""') + '"';
}

export function downloadCsv(filename, csv) {
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

// ------------------------------------------------------------------------------------------------
// Capacity maths
// ------------------------------------------------------------------------------------------------

export function bizDays(from, to) {
	const out = [];
	const d = moment(from, DATE);
	const e = moment(to, DATE);
	while (!d.isAfter(e)) {
		const w = d.isoWeekday();
		if (w <= 5) out.push(d.format(DATE));
		d.add(1, "days");
	}
	return out;
}

/**
 * Share of a task falling inside [from, to], spreading its hours evenly over its business days.
 */
export function share(task, from, to) {
	let days = bizDays(task.startDate <= task.dueDate ? task.startDate : task.dueDate, task.dueDate);
	if (!days.length) days = [task.dueDate];
	return days.filter(k => k >= from && k <= to).length / days.length;
}

export function capacityPeriods(ref = today()) {
	const d = moment(ref, DATE);
	const weekStart = d.clone().startOf("week");
	const weekEnd = weekStart.clone().add(6, "days");
	const nextStart = weekStart.clone().add(7, "days");
	const nextEnd = nextStart.clone().add(6, "days");
	const monthStart = d.clone().startOf("month");
	const monthEnd = d.clone().endOf("month");
	const nextMonthStart = monthStart.clone().add(1, "months");
	const nextMonthEnd = nextMonthStart.clone().endOf("month");
	const range = (a, b) => `${a.format("MMM D")} – ${b.format("MMM D")}`;
	return {
		thisweek: { from: weekStart.format(DATE), to: weekEnd.format(DATE), label: `This week (${range(weekStart, weekEnd)})` },
		nextweek: { from: nextStart.format(DATE), to: nextEnd.format(DATE), label: `Next week (${range(nextStart, nextEnd)})` },
		next2: { from: weekStart.format(DATE), to: nextEnd.format(DATE), label: `Next 2 weeks (${range(weekStart, nextEnd)})` },
		month: { from: monthStart.format(DATE), to: monthEnd.format(DATE), label: monthStart.format("MMMM YYYY") },
		nextmonth: { from: nextMonthStart.format(DATE), to: nextMonthEnd.format(DATE), label: nextMonthStart.format("MMMM YYYY") },
	};
}

export function capacityOf(user, from, to) {
	if (!user || user.weeklyHours === null || user.weeklyHours === undefined) return 0;
	return (user.weeklyHours * bizDays(from, to).length) / 5;
}
